import React, {useMemo, useState} from "react";
import {WhiteCard} from "../_components/WhiteCard";
import {ABOverlayPopover} from "../../_components/CustomOverlayTooltip";
import {Alert, Badge, Button, Modal} from "react-bootstrap";
import {TableLikeText} from "../../_components";
import {endpoints, formatBytes, formatTime} from "../../../utils2";
import {CustomVUMeterVisualizer} from "../VUMeterVisualizer";
import {DeviceProject} from "../../../utils2/maps";
import {useTopicSSE} from "../../../hooks/useTopicSSE";
import {SseTopics} from "../../../context/SSEContext";
import {Slider} from "../_components/Slider";
import {useAuth} from "../../../context";
import {useParams} from "react-router-dom";
import {usePostJson} from "../../../hooks";

export function CodecSection({device}) {
    const online = useTopicSSE(SseTopics.Online)
    const codec = useTopicSSE(SseTopics.Codec)
    const next = useTopicSSE(SseTopics.Next)
    const vu = useTopicSSE(SseTopics.VU)

    return <>
        {online && next?.state === 3 && (device?.device_project === DeviceProject.StandardEth ||
            device?.device_project === DeviceProject.MicroEth ||
            device?.device_project === DeviceProject.NanoEth) && <WhiteCard className="flex-grow-1">
            <CodecDetails codec={codec} vu={vu} />
        </WhiteCard>}
    </>
}

function CodecDetails({codec, vu}) {


    function getSpeed(correction, factor) {
        const speed = correction >= 0 ? (correction / factor) + 1 : -factor / (factor + correction);
        return speed.toFixed(4) + "x"
    }

    return <>
        <>

            <div className="d-flex">
                <div>
                    <ABOverlayPopover buttonLabel={<Badge bg="info" className="mb-2">
                        <div className="d-flex align-items-center gap-2">CODEC <i
                            className="fs-5 bi bi-music-note-beamed"/></div>
                    </Badge>} headerText="Additional details">
                        <div>
                            <TableLikeText titleColor="text-light" title="Decoder" value={codec?.chip ? codec.chip : ""}/>
                            <TableLikeText titleColor="text-light" title="Play time" value={codec?.dt ? formatTime(codec.dt) : ""}/>
                            <TableLikeText titleColor="text-light" title="Downloaded" value={codec?.sz ? formatBytes(codec.sz) : ""}/>
                            <TableLikeText titleColor="text-light" title="Buffer fill" value={codec?.bf ? formatBytes(codec.bf) : ""}/>
                            <TableLikeText titleColor="text-light" title="Buffer size" value={codec?.bs ? formatBytes(codec.bs) : ""}/>
                        </div>
                    </ABOverlayPopover>

                    <TableLikeText title="Item type" value={codec?.fmt}/>
                    <TableLikeText title="Channel" value={codec?.ch ? (codec.ch === 1 ? "Mono" : "Stereo") : ""}/>
                    <TableLikeText title="Bitrate" value={codec?.br ? (codec.br / 1000 + "kb/s") : ""}/>
                    <TableLikeText title="Sample rate" value={codec?.sr ? (codec.sr + "Hz") : ""}/>
                    <TableLikeText title="Bitrate (AVG)" value={codec?.avg ? `${(codec?.avg / 1000).toFixed(1)} kb/s` : ""}/>
                    <TableLikeText title="Speed" value={codec?.crt && codec?.fct ? getSpeed(codec.crt, codec.fct) : ""}/>
                    <TableLikeText title="Delay" value={codec?.bf && codec?.avg ? (codec.bf / (codec.avg / 8)).toFixed(1) + " s" : ""}/>
                </div>

                <div className="ms-auto">
                    <div className="small text-center">VU <span className="d-none d-lg-inline">Meter</span></div>
                    <ShowVUMeter vu={vu}/>

                    <VolumeModal />
                </div>
            </div>
        </>
    </>
}

function ShowVUMeter({vu}) {

    return <>
        {vu && <div className="d-flex gap-1">
            <CustomVUMeterVisualizer valueVU={vu.l}/>
            <CustomVUMeterVisualizer valueVU={vu.r}/>
        </div>}
    </>
}

function VolumeModal({}) {
    const [show, setShow] = useState(false);
    const {authToken} = useAuth()
    const {uid, did} = useParams()

    const vol = useTopicSSE(SseTopics.VOL)

    const volLeft = (vol >> 8) & 0xFF
    const volRight = (vol >> 0) & 0xFF
    const volume = volLeft < volRight ? 255 - volLeft : 255 - volRight

    const [volumePush, volumeValue, volumeLoadingPush, volumeErrorPush, clearVolumeErrorPush] = usePostJson(endpoints.push.volume(uid, did), authToken?.jwt)

    function onVolumeChange(volume) {
        volumePush({volume, balance: 0})
    }

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);


    return <>
        <Alert variant="danger" show={volumeErrorPush} dismissible onClose={clearVolumeErrorPush}>{volumeErrorPush}</Alert>

        <Button variant="link" className="p-0 mt-2" onClick={handleShow}>
            Set Volume
        </Button>

        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Set Volume</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Volume volume={volume} onVolumeChange={onVolumeChange}/>
            </Modal.Body>
            {/*<Modal.Footer>*/}
            {/*    <Button variant="secondary" onClick={handleClose}>*/}
            {/*        Close*/}
            {/*    </Button>*/}
            {/*    <Button variant="primary" onClick={handleClose}>*/}
            {/*        Save Changes*/}
            {/*    </Button>*/}
            {/*</Modal.Footer>*/}
        </Modal>
    </>
}

function Volume({volume, onVolumeChange}) {

    const [v, setV] = useState(0)

    const volumeLabel = useMemo(() => v === 0 ? "Power off" : v === 255 ? "No attenuation" : `Volume ${(v - 255) * 0.5} dB`, [v])

    return <div className="">
        <Slider name="vol" value={volume || 0} label={volumeLabel} min="0" max="255" step="1" iconLeft="bi-volume-down" iconRight="bi-volume-up" interval={75}
                timeout={550} onDone={onVolumeChange} onChange={e => setV(Number(e.target.value))} />
    </div>
}